<template>
  <div class="batch-processing-view">
    <div class="page-container">
      <div class="page-header">
        <div class="header-content">
          <h1><i class="fas fa-tasks"></i> Batch Processing</h1>
          <p class="page-subtitle">Large-scale document processing for comprehensive analysis</p>
        </div>
        <div class="header-actions">
          <button @click="showHelp = !showHelp" class="btn btn-secondary">
            <i class="fas fa-question-circle"></i> Help
          </button>
        </div>
      </div>

      <!-- Help Panel -->
      <div v-if="showHelp" class="help-panel">
        <div class="help-content">
          <h3><i class="fas fa-info-circle"></i> About Batch Processing</h3>
          <p>
            Batch Processing allows you to analyze large volumes of documents automatically.
            Perfect for client onboarding, bulk document imports, or processing historical data.
          </p>
          <div class="help-features">
            <div class="feature">
              <i class="fas fa-check-circle"></i>
              <span>Process hundreds or thousands of documents</span>
            </div>
            <div class="feature">
              <i class="fas fa-check-circle"></i>
              <span>Automatic entity matching and verification</span>
            </div>
            <div class="feature">
              <i class="fas fa-check-circle"></i>
              <span>Comprehensive compliance analysis</span>
            </div>
            <div class="feature">
              <i class="fas fa-check-circle"></i>
              <span>Real-time progress monitoring</span>
            </div>
            <div class="feature">
              <i class="fas fa-check-circle"></i>
              <span>Pause and resume anytime</span>
            </div>
          </div>
          <button @click="showHelp = false" class="btn btn-primary">
            <i class="fas fa-times"></i> Close
          </button>
        </div>
      </div>

      <!-- Project Selector -->
      <div class="project-selector-card" v-if="!selectedProjectId">
        <h3><i class="fas fa-folder"></i> Select a Project</h3>
        <p>Choose a project to manage batch processing:</p>
        
        <div v-if="loadingProjects" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i> Loading projects...
        </div>
        
        <div v-else-if="projects.length === 0" class="empty-state">
          <i class="fas fa-folder-open fa-2x"></i>
          <p>No projects found. Create a project first.</p>
          <router-link to="/projects" class="btn btn-primary">
            <i class="fas fa-plus"></i> Create Project
          </router-link>
        </div>
        
        <div v-else class="projects-grid">
          <div 
            v-for="project in projects" 
            :key="project.id" 
            @click="selectProject(project.id)"
            class="project-card">
            <div class="project-icon">
              <i class="fas fa-folder"></i>
            </div>
            <div class="project-info">
              <h4>{{ project.name }}</h4>
              <p v-if="project.description">{{ project.description }}</p>
              <div class="project-meta">
                <span><i class="fas fa-file"></i> {{ project.document_count || 0 }} documents</span>
              </div>
            </div>
            <div class="project-arrow">
              <i class="fas fa-chevron-right"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Batch Processing Manager (when project selected) -->
      <div v-else class="batch-processing-content">
        <div class="breadcrumb">
          <a @click="selectedProjectId = null" class="breadcrumb-link">
            <i class="fas fa-arrow-left"></i> Back to Projects
          </a>
          <span class="breadcrumb-separator">/</span>
          <span class="breadcrumb-current">{{ selectedProjectName }}</span>
        </div>

        <BatchProcessingManager :project-id="selectedProjectId" />
      </div>
    </div>
  </div>
</template>

<script>
import BatchProcessingManager from '@/components/batch/BatchProcessingManager.vue';
import api from '@/services/api';

export default {
  name: 'BatchProcessing',
  components: {
    BatchProcessingManager
  },
  data() {
    return {
      selectedProjectId: null,
      selectedProjectName: '',
      projects: [],
      loadingProjects: false,
      showHelp: false
    };
  },
  mounted() {
    // Check if project ID is in route params
    if (this.$route.params.projectId) {
      this.selectedProjectId = parseInt(this.$route.params.projectId);
      this.loadProjectName();
    } else if (this.$route.query.project) {
      this.selectedProjectId = parseInt(this.$route.query.project);
      this.loadProjectName();
    } else {
      this.loadProjects();
    }
  },
  methods: {
    async loadProjects() {
      this.loadingProjects = true;
      try {
        const response = await api.get('/projects/');
        // Handle different response formats
        if (Array.isArray(response.data)) {
          this.projects = response.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          this.projects = response.data.results;
        } else if (response.data.projects && Array.isArray(response.data.projects)) {
          this.projects = response.data.projects;
        } else {
          console.error('Unexpected projects response format:', response.data);
          this.projects = [];
        }
      } catch (error) {
        console.error('Error loading projects:', error);
        this.$emit('error', 'Failed to load projects');
        this.projects = [];
      } finally {
        this.loadingProjects = false;
      }
    },
    async loadProjectName() {
      try {
        const response = await api.get(`/projects/${this.selectedProjectId}/`);
        this.selectedProjectName = response.data.name;
      } catch (error) {
        console.error('Error loading project name:', error);
        this.selectedProjectName = `Project ${this.selectedProjectId}`;
      }
    },
    selectProject(projectId) {
      this.selectedProjectId = projectId;
      
      // Safely find project
      if (Array.isArray(this.projects) && this.projects.length > 0) {
        const project = this.projects.find(p => p.id === projectId);
        this.selectedProjectName = project ? project.name : `Project ${projectId}`;
      } else {
        this.selectedProjectName = `Project ${projectId}`;
      }
      
      // Update URL without reloading
      this.$router.push({ 
        name: 'BatchProcessing', 
        query: { project: projectId } 
      }).catch(err => {
        // Ignore navigation duplicated errors
        if (err.name !== 'NavigationDuplicated') {
          console.error('Navigation error:', err);
        }
      });
    }
  }
};
</script>

<style scoped>
.batch-processing-view {
  min-height: 100vh;
  background: var(--bg-secondary, #f5f7fa);
}

.page-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: var(--spacing-xl, 2rem);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-xl, 2rem);
  padding-bottom: var(--spacing-lg, 1.5rem);
  border-bottom: 2px solid var(--border-color, #e1e8ed);
}

.header-content h1 {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary, #1a1a1a);
  margin: 0 0 var(--spacing-sm, 0.5rem) 0;
}

.header-content h1 i {
  color: var(--primary-color, #0066cc);
  margin-right: var(--spacing-sm, 0.5rem);
}

.page-subtitle {
  font-size: 1rem;
  color: var(--text-secondary, #666);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: var(--spacing-md, 1rem);
}

/* Help Panel */
.help-panel {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: var(--spacing-xl, 2rem);
  border-radius: 12px;
  margin-bottom: var(--spacing-xl, 2rem);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.help-content h3 {
  margin: 0 0 var(--spacing-md, 1rem) 0;
  font-size: 1.5rem;
}

.help-content h3 i {
  margin-right: var(--spacing-sm, 0.5rem);
}

.help-content p {
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: var(--spacing-lg, 1.5rem);
  opacity: 0.95;
}

.help-features {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-md, 1rem);
  margin-bottom: var(--spacing-lg, 1.5rem);
}

.help-features .feature {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm, 0.5rem);
  font-size: 0.95rem;
}

.help-features .feature i {
  color: #4ade80;
  font-size: 1.2rem;
}

/* Project Selector */
.project-selector-card {
  background: white;
  padding: var(--spacing-xl, 2rem);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.project-selector-card h3 {
  margin: 0 0 var(--spacing-sm, 0.5rem) 0;
  color: var(--text-primary, #1a1a1a);
}

.project-selector-card h3 i {
  color: var(--primary-color, #0066cc);
  margin-right: var(--spacing-sm, 0.5rem);
}

.project-selector-card > p {
  color: var(--text-secondary, #666);
  margin-bottom: var(--spacing-lg, 1.5rem);
}

.projects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--spacing-md, 1rem);
  margin-top: var(--spacing-lg, 1.5rem);
}

.project-card {
  display: flex;
  align-items: center;
  gap: var(--spacing-md, 1rem);
  padding: var(--spacing-lg, 1.5rem);
  background: var(--bg-secondary, #f5f7fa);
  border: 2px solid var(--border-color, #e1e8ed);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.project-card:hover {
  border-color: var(--primary-color, #0066cc);
  background: white;
  box-shadow: 0 4px 12px rgba(0, 102, 204, 0.1);
  transform: translateY(-2px);
}

.project-icon {
  font-size: 2rem;
  color: var(--primary-color, #0066cc);
  flex-shrink: 0;
}

.project-info {
  flex: 1;
}

.project-info h4 {
  margin: 0 0 var(--spacing-xs, 0.25rem) 0;
  font-size: 1.1rem;
  color: var(--text-primary, #1a1a1a);
}

.project-info p {
  margin: 0 0 var(--spacing-sm, 0.5rem) 0;
  font-size: 0.9rem;
  color: var(--text-secondary, #666);
}

.project-meta {
  display: flex;
  gap: var(--spacing-md, 1rem);
  font-size: 0.85rem;
  color: var(--text-tertiary, #999);
}

.project-meta i {
  margin-right: var(--spacing-xs, 0.25rem);
}

.project-arrow {
  font-size: 1.5rem;
  color: var(--text-tertiary, #999);
  flex-shrink: 0;
}

.project-card:hover .project-arrow {
  color: var(--primary-color, #0066cc);
}

/* Loading & Empty States */
.loading-state,
.empty-state {
  text-align: center;
  padding: var(--spacing-xl, 2rem);
  color: var(--text-secondary, #666);
}

.loading-state i,
.empty-state i {
  color: var(--text-tertiary, #999);
  margin-bottom: var(--spacing-md, 1rem);
}

.empty-state p {
  margin: var(--spacing-md, 1rem) 0;
}

/* Breadcrumb */
.breadcrumb {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm, 0.5rem);
  margin-bottom: var(--spacing-lg, 1.5rem);
  font-size: 0.9rem;
}

.breadcrumb-link {
  color: var(--primary-color, #0066cc);
  cursor: pointer;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs, 0.25rem);
}

.breadcrumb-link:hover {
  text-decoration: underline;
}

.breadcrumb-separator {
  color: var(--text-tertiary, #999);
}

.breadcrumb-current {
  color: var(--text-secondary, #666);
  font-weight: 600;
}

/* Batch Processing Content */
.batch-processing-content {
  /* The BatchProcessingManager component has its own styling */
}

/* Responsive */
@media (max-width: 768px) {
  .page-container {
    padding: var(--spacing-md, 1rem);
  }

  .page-header {
    flex-direction: column;
    gap: var(--spacing-md, 1rem);
  }

  .projects-grid {
    grid-template-columns: 1fr;
  }

  .help-features {
    grid-template-columns: 1fr;
  }
}
</style>

